import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCountry } from "../contexts/CountryContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import BMIGauge from "../components/BMIGauge";
import ProductCard from "../components/ProductCard";
import { Heart, MessageCircle, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { track } from "../lib/analytics";
import { toast } from "sonner";
import HowWeChooseBlock from "../components/HowWeChooseBlock";
import WellnessPlanEmailCTA from "../components/WellnessPlanEmailCTA";

const levelCls = {
  low: "bg-emerald-100 text-emerald-800",
  moderate: "bg-amber-100 text-amber-800",
  high: "bg-red-100 text-red-700",
};

export default function ReportPage() {
  const navigate = useNavigate();
  const ctx = useCountry();
  const country = ctx?.country || "UK";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsapp, setWhatsapp] = useState("");

  const fetchReport = async () => {
    setLoading(true);
    try {
      const [{ data: cfg }, { data: rec }] = await Promise.all([
        api.get("/config"),
        api.get(`/recommendations?country=${encodeURIComponent(country)}`),
      ]);
      setWhatsapp(cfg.whatsapp_number || "");
      setData(rec);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.info("Complete your assessment first.");
        navigate("/questionnaire", { replace: true });
      } else {
        toast.error("Couldn't load report. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center text-emerald-700" data-testid="report-loading">
        <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Generating your personalized plan…
      </main>
    );
  }
  if (!data) return null;

  const { assessment, summary, recommendations } = data;
  const hasCustomConcern = !!(assessment?.conditions_other && assessment.conditions_other.trim().length >= 4);
  const waMsg = encodeURIComponent(
    hasCustomConcern
      ? "Hi, I completed my health assessment and shared a custom concern. Can you guide me further?"
      : "Hi, I completed my health assessment. Can you guide me further?"
  );
  const waUrl = whatsapp ? `https://wa.me/${whatsapp}?text=${waMsg}` : `https://wa.me/?text=${waMsg}`;

  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-10" data-testid="report-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            Your personalized health report
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-emerald-950 font-light tracking-tight mt-2">
            Here's what we found — and what to do next.
          </h1>
        </div>

        {hasCustomConcern ? (
          <div
            className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-5 py-4 flex items-start gap-3"
            data-testid="report-personal-followup-banner"
          >
            <Sparkles className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-900 leading-relaxed">
              <span className="font-semibold">Thanks for sharing a little more about your health journey.</span>{" "}
              Our wellness team may review your answers to help guide you more personally.
              You'll continue to see your full report below.
            </p>
          </div>
        ) : null}

        {/* Summary + BMI */}
        <div className="grid md:grid-cols-5 gap-6">
          <Card className="md:col-span-3 rounded-2xl border-emerald-100">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
                <Heart className="w-3.5 h-3.5" /> Health summary
              </div>
              <p className="text-lg text-emerald-950/90 leading-relaxed" data-testid="report-summary">
                {summary}
              </p>
              <div className="mt-6 flex items-center gap-3 text-sm text-emerald-900/60">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                Guidance only. For chronic conditions, always consult your physician.
              </div>
            </CardContent>
          </Card>
          <Card className="md:col-span-2 rounded-2xl border-emerald-100">
            <CardContent className="p-6 sm:p-8">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">
                BMI result
              </div>
              <BMIGauge bmi={assessment.bmi} category={assessment.bmi_category} />
              <div className="mt-4 text-xs text-emerald-900/60 text-center">
                BMI is one signal among many. Context matters.
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risk indicators */}
        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                  Risk indicators
                </div>
                <h2 className="font-display text-xl sm:text-2xl text-emerald-950 mt-1">
                  Where to focus your energy
                </h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {assessment.risk_indicators?.map((r) => (
                <div
                  key={r.key}
                  className="rounded-xl border border-emerald-100 p-4 bg-white"
                  data-testid={`risk-${r.key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-emerald-950">{r.name}</div>
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full ${
                        levelCls[r.level] || levelCls.low
                      }`}
                    >
                      {r.level}
                    </span>
                  </div>
                  <div className="text-xs text-emerald-900/70 mt-2">{r.note}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <section className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                Matched products
              </div>
              <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 mt-1">
                Your Feel Great plan
              </h2>
            </div>
            <Button
              variant="ghost"
              onClick={fetchReport}
              className="rounded-full text-emerald-800 hover:bg-emerald-50"
              data-testid="refresh-recommendations-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>

          <HowWeChooseBlock />

          <div className="space-y-4">
            {recommendations?.map((r) => (
              <ProductCard key={r.product.id} rec={r} whatsappNumber={whatsapp} />
            ))}
          </div>
        </section>

        {/* Soft-conversion: email-only PDF capture (works for guests + registered) */}
        <WellnessPlanEmailCTA
          assessmentId={assessment?.id}
          suggestedName={assessment?.name}
        />

        {/* Global Expert CTA — amplified when user shared a custom concern */}
        <Card className={`rounded-2xl ${hasCustomConcern ? "border-emerald-200 bg-emerald-50/70 ring-1 ring-emerald-200" : "border-amber-200 bg-amber-50/70"}`} data-testid="report-expert-cta">
          <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-xl text-emerald-950">
                {hasCustomConcern ? "Your concern deserves personalised guidance." : "Need a human second opinion?"}
              </h3>
              <p className="text-sm text-emerald-900/70 mt-1">
                {hasCustomConcern
                  ? "Some health concerns benefit from a one-on-one conversation. Our certified coaches will read what you shared and respond with tailored next steps — no judgement, no pressure."
                  : "Chat with a certified Feel Great coach — they'll review your report and guide you live."}
              </p>
            </div>
            <a href={waUrl} target="_blank" rel="noreferrer noopener" data-testid="global-whatsapp-cta" onClick={() => track.whatsappClick("report_page", null)}>
              <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6">
                <MessageCircle className="w-4 h-4 mr-2" />
                {hasCustomConcern ? "Talk to a Feel Great coach" : "Talk to an expert on WhatsApp"}
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
